import { isDefined } from "@clipboard-health/util-ts";
import { Box, Skeleton, type SxProps, type Theme } from "@mui/material";
import { useCase } from "@src/appV2/ExperimentalHomeHealth/Cases/api/useCase";
import { useVisit } from "@src/appV2/ExperimentalHomeHealth/Cases/api/useVisit";
import { VISIT_TYPE_LABELS, type VisitType } from "@src/appV2/ExperimentalHomeHealth/Cases/types";

import { ChatMessageCard } from "./ChatMessageCard";

interface VisitInviteRequestMessageProps {
  visitId: string | undefined;
  sx?: SxProps<Theme>;
}

/**
 * Renders a visit invite request message for Home Health & Hospice workplaces.
 * Shows visit type, worker requirement, and patient identifier.
 */
export function VisitInviteRequestMessage(props: VisitInviteRequestMessageProps) {
  const { visitId, sx } = props;

  const { data: visit, isLoading: isVisitLoading, isError: isVisitError } = useVisit(visitId);
  const { caseId, type: visitType, workerReq } = visit?.attributes ?? {};

  const {
    data: caseRecord,
    isLoading: isCaseLoading,
    isError: isCaseError,
  } = useCase(caseId ?? undefined);

  const patient = caseRecord?.attributes.patient;
  const patientName = patient?.attributes.externalPatientIdentifier;

  // Don't render if there's an error or missing required data
  if (isVisitError || isCaseError || !isDefined(visitId) || !isDefined(caseId)) {
    return null;
  }

  // Show loading skeleton while fetching data
  if (isVisitLoading || isCaseLoading) {
    return <Skeleton height="4rem" sx={{ margin: 2 }} variant="rounded" width="100%" />;
  }

  const title = `${visitType ? VISIT_TYPE_LABELS[visitType as VisitType] : ""} ${workerReq} visit invite sent for ${patientName}`;

  return (
    <Box data-dd-privacy="mask">
      <ChatMessageCard sx={sx} title={title} />
    </Box>
  );
}
